<template>
  <div
    class="wg-site-card"
    :class="{ 'wg-site-card--selected': isSelected }"
    @click="goToTools">
    <div class="wg-site-card__header d-flex align-center">
      <v-checkbox
        class="ma-0 wg-site-card__header-checkbox"
        color="success"
        off-icon="none"
        on-icon="check"
        :ripple="false"
        hide-details
        :input-value="isSelected"
        :disabled="isDemoUser"
        aria-label="Select project"
        @click.native.stop
        @change="onSelectionChange"
      />
      <div
        class="wg-site-card__name wg-site-card__name--header truncate-ellipse-one-line"
        :title="site.name">
        {{ site.name }}
      </div>
      <v-tooltip top color="transparent">
        <template v-slot:activator="{ on, attrs }">
          <span
            v-bind="attrs"
            v-on="on"
            class="material-icons pin-icon wg-site-card__header-pin"
            :class="{ pinned: site.isPinned }"
            @click.stop="() => $store.getters.isDemoUser ? null : $emit('pin', { siteId: site.id, isPinned: !site.isPinned })">push_pin</span>
        </template>
        <div class="white-bg-tooltip small-tooltip">
          {{
            site.isPinned ? 'Click to unpin' : 'Pin this Project to the top of your dashboard to find it easily.'
          }}
        </div>
      </v-tooltip>
      <div class="wg-site-card__menu-wrap" @click.stop>
        <v-btn icon :id="`wg-dot-menu-for-${site.id}`">
          <span class="material-icons-outlined darkGrayColor--text">more_vert</span>
        </v-btn>
        <v-menu
          v-if="!isDemo"
          :activator="`#wg-dot-menu-for-${site.id}`"
          bottom
          offset-y>
          <v-list>
            <v-list-item link @click="$store.getters.isDemoUser ? null : $emit('open-settings', site)">
              <v-list-item-title class="mainColor--text">
                Project setting
              </v-list-item-title>
            </v-list-item>
            <v-list-item link @click="$store.getters.isDemoUser ? null : $emit('delete', site.id)">
              <v-list-item-title class="mainColor--text">
                Delete
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
    </div>
    <div class="wg-site-card__body">
      <div class="wg-site-card__status">
        <span
          class="wg-status-dot"
          :style="dotStyle"
          :title="insight.statusLabel"
        ></span>
        <span>{{ insight.statusLabel }}</span>
      </div>
      <div class="wg-site-card__orders">
        <strong>{{ insight.activeOrdersCount }}</strong>
        {{ insight.activeOrdersCount === 1 ? 'active order' : 'active orders' }}
      </div>
    </div>
    <div
      v-if="insight.showBadge"
      class="wg-site-card__badge"
      :style="badgeChipStyle">
      {{ insight.badgeLabel }}
    </div>
    <!-- POP Authority: live package tag ("Growth · Month 2") when no action badge is shown -->
    <div
      v-else-if="insight.packageLabel"
      class="wg-site-card__badge wg-site-card__badge--package"
      :title="`Package: ${insight.packageLabel}`">
      {{ insight.packageLabel }}
    </div>
  </div>
</template>

<script>
import { whiteGloveStatusDotStyle } from '@/utils/whiteGloveSiteList'

const FALLBACK_DOT = '#9e9e9e'

function hexToRgba (hex, alpha) {
  let h = String(hex || '').trim()
  if (!h) h = FALLBACK_DOT
  if (h[0] === '#') h = h.slice(1)
  if (h.length === 3) {
    h = h.split('').map((c) => c + c).join('')
  }
  if (h.length !== 6 || !/^[0-9a-fA-F]+$/.test(h)) {
    h = '9e9e9e'
  }
  const r = parseInt(h.slice(0, 2), 16)
  const g = parseInt(h.slice(2, 4), 16)
  const b = parseInt(h.slice(4, 6), 16)
  return `rgba(${r},${g},${b},${alpha})`
}

export default {
  props: {
    site: { type: Object, required: true },
    insight: {
      type: Object,
      required: true,
    },
    isSelected: { type: Boolean, default: false },
    isDemo: { type: Boolean, default: false },
    createdAt: { type: String, default: '' },
  },
  computed: {
    isDemoUser () {
      return this.$store.getters.isDemoUser
    },
    dotStyle () {
      return whiteGloveStatusDotStyle(this.insight.dotColor)
    },
    badgeChipStyle () {
      const fg = this.insight.dotColor || FALLBACK_DOT
      return {
        color: fg,
        backgroundColor: hexToRgba(fg, 0.12),
      }
    },
  },
  methods: {
    goToTools () {
      this.$router.push(`/sites/${this.site.id}/tools`)
    },
    onSelectionChange (checked) {
      if (this.isDemoUser) return
      if (checked) {
        this.$emit('select', this.site.id)
      } else {
        this.$emit('unselect', this.site.id)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.wg-site-card {
  position: relative;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid var(--v-gray12Color-base);
  padding: 20px 24px;
  cursor: pointer;
  transition: box-shadow 0.2s, border-color 0.2s;

  &:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  }

  &--selected {
    border-color: var(--v-profileAnchorColor-base);
    box-shadow: 0 0 0 1px var(--v-profileAnchorColor-base), 0 2px 8px rgba(0, 0, 0, 0.08);
  }

  &__header {
    gap: 10px;
    margin: -4px 0 10px;
    align-items: center;
  }

  &__header-checkbox {
    flex-shrink: 0;
    align-self: center;
    padding-top: 0;
    margin-top: 0 !important;
  }

  ::v-deep .wg-site-card__header-checkbox {
    label {
      color: var(--v-mainColor-base);
      font-size: 0.9rem;
    }

    .v-input__slot {
      padding: 0 !important;
    }

    .v-input--selection-controls__input {
      border: 1px solid #999999 !important;
      border-radius: 4px;
      height: 20px !important;
      width: 20px !important;
      margin-right: 0 !important;
    }

    &.v-input--is-label-active {
      .v-icon {
        font-size: 16px !important;
      }
    }
  }

  &__header-pin {
    flex-shrink: 0;
  }

  &__name--header {
    flex: 1;
    min-width: 0;
    font-size: 16px;
    font-weight: 700;
    color: #1a3d5c;
    line-height: 1.3;
  }

  &__menu-wrap {
    flex-shrink: 0;
    margin: -8px -8px -8px 0;
  }

  &__body {
    min-height: 48px;
  }

  &__badge {
    position: absolute;
    bottom: 0;
    right: 0;
    font-size: 12px;
    font-weight: 600;
    padding: 4px 12px;
    border-radius: 12px 0 12px 0;
    cursor: pointer;

    &--package {
      cursor: default;
      color: #5b2d7a;
      background: #f1e6f6;
      border: 1px solid #d9bfe6;
      border-right: 0;
      border-bottom: 0;
    }
  }

  &__status {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    color: #444;
    margin-bottom: 6px;
  }

  &__orders {
    font-size: 14px;
    color: #444;

    strong {
      color: #1a3d5c;
    }
  }
}
</style>
